import { useState, useEffect, useMemo } from 'react';
import { Page, Card, Text, Spinner, Banner, InlineStack, Box, Button, Collapsible, Icon, Badge, Link, TextField, Select, DataTable } from '@shopify/polaris';
import { ChevronDownIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import { getRequest } from '../../shared/components/functions.jsx';
import { TabTitle } from '../../shell/helmet.jsx';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '€0.00';
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatNumber = (value) => {
  if (value == null || isNaN(value)) return '0';
  return new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 }).format(value);
};

// Summary card component for totals
function SummaryCard({ title, value, subtitle, tone }) {
  return (
    <Card>
      <Box padding="400">
        <Text variant="headingXs" tone="subdued">
          {title}
        </Text>
        <Box paddingBlockStart="100">
          <Text variant="headingLg" fontWeight="bold" tone={tone}>
            {value}
          </Text>
        </Box>
        {subtitle && (
          <Box paddingBlockStart="100">
            <Text variant="bodySm" tone="subdued">
              {subtitle}
            </Text>
          </Box>
        )}
      </Box>
    </Card>
  );
}

// Expandable row for picking place
function PickingPlaceRow({ place, isExpanded, onToggle, expandedRacks, onToggleRack, searchTerm }) {
  const filteredRacks = useMemo(() => {
    if (!searchTerm) return place.racks;
    return place.racks
      .map((rack) => ({
        ...rack,
        products: rack.products.filter(
          (p) =>
            p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(p.id).includes(searchTerm) ||
            p.supplier?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }))
      .filter((rack) => rack.products.length > 0);
  }, [place.racks, searchTerm]);

  const hasMatchingProducts = searchTerm ? filteredRacks.length > 0 : true;

  if (!hasMatchingProducts) return null;

  const totalValue = place.activeValue + place.discontinuedValue;
  const totalItems = place.activeItems + place.discontinuedItems;
  const totalQuantity = place.activeQuantity + place.discontinuedQuantity;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          backgroundColor: '#f6f6f7',
          borderBottom: '1px solid #e1e3e5'
        }}
      >
        <td style={{ padding: '12px', width: '40px' }}>
          <Icon source={isExpanded ? ChevronDownIcon : ChevronRightIcon} />
        </td>
        <td style={{ padding: '12px' }}>
          <Text variant="headingSm" fontWeight="semibold">
            {place.name}
          </Text>
        </td>
        <td style={{ padding: '12px', textAlign: 'right' }}>
          <Text variant="bodySm">{formatNumber(totalQuantity)}</Text>
        </td>
        <td style={{ padding: '12px', textAlign: 'right' }}>
          <Text variant="bodySm">{totalItems} products</Text>
        </td>
        <td style={{ padding: '12px', textAlign: 'right' }}>
          <Text variant="bodyMd" fontWeight="semibold" tone="success">
            {formatCurrency(place.activeValue)}
          </Text>
        </td>
        <td style={{ padding: '12px', textAlign: 'right' }}>
          <Text variant="bodyMd" tone="critical">
            {formatCurrency(place.discontinuedValue)}
          </Text>
        </td>
      </tr>
      <tr>
        <td colSpan={6} style={{ padding: 0 }}>
          <Collapsible open={isExpanded} transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}>
            <Box paddingInlineStart="800" paddingBlockEnd="200">
              {filteredRacks.map((rack) => (
                <RackRow
                  key={`${place.id}-${rack.rack}`}
                  rack={rack}
                  placeId={place.id}
                  placeName={place.name}
                  isExpanded={expandedRacks[`${place.id}-${rack.rack}`]}
                  onToggle={() => onToggleRack(`${place.id}-${rack.rack}`)}
                  searchTerm={searchTerm}
                />
              ))}
            </Box>
          </Collapsible>
        </td>
      </tr>
    </>
  );
}

// Expandable row for rack
function RackRow({ rack, placeId, placeName, isExpanded, onToggle, searchTerm }) {
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return rack.products;
    return rack.products.filter(
      (p) =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(p.id).includes(searchTerm) ||
        p.supplier?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [rack.products, searchTerm]);

  const totalValue = rack.activeValue + rack.discontinuedValue;
  const totalItems = rack.activeItems + rack.discontinuedItems;
  const totalQuantity = rack.activeQuantity + rack.discontinuedQuantity;

  return (
    <>
      <div
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          padding: '8px 12px',
          backgroundColor: '#fafbfb',
          borderBottom: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <Icon source={isExpanded ? ChevronDownIcon : ChevronRightIcon} />
        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="info">Rack {rack.rack}</Badge>
            <Text variant="bodySm" tone="subdued">
              {totalItems} products
            </Text>
          </InlineStack>
          <InlineStack gap="400">
            <Text variant="bodySm">{formatNumber(totalQuantity)} qty</Text>
            <Text variant="bodySm" fontWeight="semibold" tone="success">
              {formatCurrency(rack.activeValue)}
            </Text>
            {rack.discontinuedValue > 0 && (
              <Text variant="bodySm" tone="critical">
                {formatCurrency(rack.discontinuedValue)} disc.
              </Text>
            )}
          </InlineStack>
        </div>
      </div>
      <Collapsible open={isExpanded} transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}>
        <Box paddingInlineStart="600" paddingBlockEnd="200">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500 }}>ID</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500 }}>Reference</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500 }}>Product</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500 }}>Supplier</th>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 500 }}>Shelf</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>Qty</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>Purchase</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr
                  key={product.id}
                  style={{
                    borderBottom: '1px solid #f1f1f1',
                    backgroundColor: product.discontinued ? '#fee2e2' : 'transparent'
                  }}
                >
                  <td style={{ padding: '6px 8px' }}>
                    <Link removeUnderline url={`/products/${product.id}`}>
                      #{product.id}
                    </Link>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <Text variant="bodySm" tone="subdued">
                      {product.ref || '-'}
                    </Text>
                  </td>
                  <td style={{ padding: '6px 8px', maxWidth: '200px' }}>
                    <InlineStack gap="100" blockAlign="center">
                      <Text variant="bodySm" truncate>
                        {product.name || '-'}
                      </Text>
                      {product.discontinued && (
                        <Badge tone="critical" size="small">
                          Discontinued
                        </Badge>
                      )}
                    </InlineStack>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    {product.supplierId ? (
                      <Link removeUnderline url={`/companies/${product.supplierId}`}>
                        <Text variant="bodySm">{product.supplier || '-'}</Text>
                      </Link>
                    ) : (
                      <Text variant="bodySm" tone="subdued">
                        -
                      </Text>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <Text variant="bodySm">{product.shelf || '-'}</Text>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <Text variant="bodySm">{formatNumber(product.quantity)}</Text>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <Text variant="bodySm" tone="subdued">
                      {formatCurrency(product.purchasePrice)}
                    </Text>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <Text variant="bodySm" fontWeight="medium">
                      {formatCurrency(product.stockValue)}
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Collapsible>
    </>
  );
}

// Supplier breakdown table
function SupplierBreakdownTable({ data }) {
  if (!data || data.length === 0) return null;

  const rows = data.map((item) => [
    item.SupplierName || 'Unknown',
    formatNumber(item.ActiveProductCount),
    formatNumber(item.ActiveQuantity),
    formatCurrency(item.ActiveValue),
    item.DiscontinuedProductCount > 0 ? formatNumber(item.DiscontinuedProductCount) : '-',
    item.DiscontinuedValue > 0 ? formatCurrency(item.DiscontinuedValue) : '-'
  ]);

  const totals = data.reduce(
    (acc, item) => ({
      activeProducts: acc.activeProducts + (item.ActiveProductCount || 0),
      activeQty: acc.activeQty + (item.ActiveQuantity || 0),
      activeValue: acc.activeValue + (item.ActiveValue || 0),
      discProducts: acc.discProducts + (item.DiscontinuedProductCount || 0),
      discValue: acc.discValue + (item.DiscontinuedValue || 0)
    }),
    { activeProducts: 0, activeQty: 0, activeValue: 0, discProducts: 0, discValue: 0 }
  );

  return (
    <Card>
      <Box padding="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm">Value by Supplier</Text>
          <InlineStack gap="400">
            <Badge tone="success">Active: {formatCurrency(totals.activeValue)}</Badge>
            {totals.discValue > 0 && <Badge tone="critical">Discontinued: {formatCurrency(totals.discValue)}</Badge>}
          </InlineStack>
        </InlineStack>
      </Box>
      <DataTable
        columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
        headings={['Supplier', 'Active Products', 'Active Qty', 'Active Value', 'Disc. Products', 'Disc. Value']}
        rows={rows}
        footerContent={`${data.length} suppliers`}
      />
    </Card>
  );
}

export default function InventoryValueOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPlaces, setExpandedPlaces] = useState({});
  const [expandedRacks, setExpandedRacks] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('value');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [loadingSupplier, setLoadingSupplier] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const result = await getRequest('/api/inventory/value/overview');
        if (result) {
          setData(result);
        }
      } catch (err) {
        console.error('Error fetching inventory value:', err);
        setError('Failed to load inventory value data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Fetch supplier breakdown when a place is selected
  useEffect(() => {
    async function fetchSupplierData() {
      if (!selectedPlace) {
        setSupplierData(null);
        return;
      }
      setLoadingSupplier(true);
      try {
        const result = await getRequest(`/api/inventory/value/by-supplier/${selectedPlace}`);
        if (result) {
          setSupplierData(result);
        }
      } catch (err) {
        console.error('Error fetching supplier breakdown:', err);
      } finally {
        setLoadingSupplier(false);
      }
    }
    fetchSupplierData();
  }, [selectedPlace]);

  const sortedPlaces = useMemo(() => {
    if (!data?.pickingPlaces) return [];
    const places = [...data.pickingPlaces];
    switch (sortBy) {
      case 'value':
        return places.sort((a, b) => b.activeValue - a.activeValue);
      case 'name':
        return places.sort((a, b) => a.name.localeCompare(b.name));
      case 'items':
        return places.sort((a, b) => b.activeItems + b.discontinuedItems - (a.activeItems + a.discontinuedItems));
      case 'quantity':
        return places.sort((a, b) => b.activeQuantity + b.discontinuedQuantity - (a.activeQuantity + a.discontinuedQuantity));
      default:
        return places;
    }
  }, [data, sortBy]);

  const togglePlace = (placeId) => {
    setExpandedPlaces((prev) => ({
      ...prev,
      [placeId]: !prev[placeId]
    }));
    setSelectedPlace((prev) => (prev === placeId ? null : placeId));
  };

  const toggleRack = (rackKey) => {
    setExpandedRacks((prev) => ({
      ...prev,
      [rackKey]: !prev[rackKey]
    }));
  };

  const expandAll = () => {
    const newExpandedPlaces = {};
    const newExpandedRacks = {};
    sortedPlaces.forEach((place) => {
      newExpandedPlaces[place.id] = true;
      place.racks.forEach((rack) => {
        newExpandedRacks[`${place.id}-${rack.rack}`] = true;
      });
    });
    setExpandedPlaces(newExpandedPlaces);
    setExpandedRacks(newExpandedRacks);
  };

  const collapseAll = () => {
    setExpandedPlaces({});
    setExpandedRacks({});
  };

  if (loading) {
    return (
      <Page title="Inventory Value" fullWidth>
        <Card>
          <Box padding="800">
            <InlineStack align="center" gap="200">
              <Spinner size="large" />
              <Text>Loading inventory value data...</Text>
            </InlineStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Inventory Value" fullWidth>
        <Banner tone="critical">{error}</Banner>
      </Page>
    );
  }

  const grandTotal = data?.grandTotal || {};
  const totalValue = grandTotal.activeValue + grandTotal.discontinuedValue;
  const totalItems = grandTotal.activeItems + grandTotal.discontinuedItems;
  const totalQuantity = grandTotal.activeQuantity + grandTotal.discontinuedQuantity;

  return (
    <Page title="Inventory Value Overview" fullWidth backAction={{ content: 'Inventory', url: '/inventory' }}>
      <TabTitle title="Inventory Value" />

      {/* Summary Cards */}
      <Box paddingBlockEnd="400">
        <InlineStack gap="400" wrap={false}>
          <div style={{ flex: 1 }}>
            <SummaryCard
              title="Active Stock Value"
              value={formatCurrency(grandTotal.activeValue)}
              subtitle={`${formatNumber(grandTotal.activeItems)} products, ${formatNumber(grandTotal.activeQuantity)} units`}
              tone="success"
            />
          </div>
          <div style={{ flex: 1 }}>
            <SummaryCard
              title="Discontinued Stock Value"
              value={formatCurrency(grandTotal.discontinuedValue)}
              subtitle={`${formatNumber(grandTotal.discontinuedItems)} products, ${formatNumber(grandTotal.discontinuedQuantity)} units`}
              tone="critical"
            />
          </div>
          <div style={{ flex: 1 }}>
            <SummaryCard title="Total Stock Value" value={formatCurrency(totalValue)} subtitle={`${formatNumber(totalItems)} products total`} />
          </div>
          <div style={{ flex: 1 }}>
            <SummaryCard title="Picking Places" value={sortedPlaces.length} subtitle={`${formatNumber(totalQuantity)} total units`} />
          </div>
        </InlineStack>
      </Box>

      {/* Main Table */}
      <Card>
        <Box padding="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd">Breakdown by Picking Place</Text>
            <InlineStack gap="300">
              <div style={{ width: '200px' }}>
                <TextField
                  label="Search"
                  labelHidden
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={setSearchTerm}
                  clearButton
                  onClearButtonClick={() => setSearchTerm('')}
                  autoComplete="off"
                />
              </div>
              <div style={{ width: '150px' }}>
                <Select
                  label="Sort by"
                  labelHidden
                  options={[
                    { label: 'Sort by Value', value: 'value' },
                    { label: 'Sort by Name', value: 'name' },
                    { label: 'Sort by Items', value: 'items' },
                    { label: 'Sort by Quantity', value: 'quantity' }
                  ]}
                  value={sortBy}
                  onChange={setSortBy}
                />
              </div>
              <Button onClick={expandAll} size="slim">
                Expand All
              </Button>
              <Button onClick={collapseAll} size="slim">
                Collapse All
              </Button>
            </InlineStack>
          </InlineStack>
        </Box>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e1e3e5', backgroundColor: '#f6f6f7' }}>
                <th style={{ padding: '12px', width: '40px' }}></th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Picking Place</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Quantity</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Products</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Active Value</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Discontinued Value</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlaces.map((place) => (
                <PickingPlaceRow
                  key={place.id}
                  place={place}
                  isExpanded={expandedPlaces[place.id]}
                  onToggle={() => togglePlace(place.id)}
                  expandedRacks={expandedRacks}
                  onToggleRack={toggleRack}
                  searchTerm={searchTerm}
                />
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #e1e3e5', backgroundColor: '#f6f6f7' }}>
                <td style={{ padding: '12px' }}></td>
                <td style={{ padding: '12px' }}>
                  <Text variant="headingSm" fontWeight="bold">
                    Grand Total
                  </Text>
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <Text variant="bodyMd" fontWeight="bold">
                    {formatNumber(totalQuantity)}
                  </Text>
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <Text variant="bodyMd" fontWeight="bold">
                    {totalItems} products
                  </Text>
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <Text variant="headingSm" fontWeight="bold" tone="success">
                    {formatCurrency(grandTotal.activeValue)}
                  </Text>
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <Text variant="headingSm" fontWeight="bold" tone="critical">
                    {formatCurrency(grandTotal.discontinuedValue)}
                  </Text>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Supplier Breakdown */}
      {selectedPlace && (
        <Box paddingBlockStart="400">
          {loadingSupplier ? (
            <Card>
              <Box padding="400">
                <InlineStack gap="200">
                  <Spinner size="small" />
                  <Text>Loading supplier breakdown...</Text>
                </InlineStack>
              </Box>
            </Card>
          ) : (
            <SupplierBreakdownTable data={supplierData} />
          )}
        </Box>
      )}
    </Page>
  );
}
